import React, { useState, useCallback } from 'react';

// This component receives logoFile and onLogoSelect as props from App.js
const LogoUploadPage = ({ logoFile, onLogoSelect, showInlineBlueButton }) => { 
  // Local state for UI feedback within this component (drag state, validation errors)
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Wrap handleFileValidation in useCallback since it's used in onDrop's dependencies
  const handleFileValidation = useCallback((selectedFile) => {
    if (selectedFile && selectedFile.type.startsWith('image/')) {
      onLogoSelect(selectedFile); // Inform App.js about the valid file
      setError(null); // Clear local errors
    } else {
      onLogoSelect(null); // Inform App.js that the selection is invalid or cleared
      setError('Please select a valid image file (PNG, JPG, GIF, etc.).');
    }
  }, [onLogoSelect]);

  // Drag event handlers for visual feedback
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((event) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  // Add handleFileValidation to the dependency array of onDrop
  const onDrop = useCallback((event) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFileValidation(event.dataTransfer.files[0]);
      event.dataTransfer.clearData(); // Recommended for some browsers
    }
  }, [handleFileValidation]); // Now depends on the memoized handleFileValidation

  // handleFileInputChange also uses handleFileValidation
  const handleFileInputChange = useCallback((event) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFileValidation(event.target.files[0]);
    }
  }, [handleFileValidation]); // Also depends on memoized handleFileValidation
  
  // Base style for the interactive drop zone area
  const dropZoneBaseStyle = {
    border: '2px dashed #ccc',
    borderRadius: '8px',
    padding: '30px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.3s ease, background-color 0.3s ease',
    height: '250px', // Height for the dropzone/confirmation box
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f9f9f9',
    width: '100%' // Takes full width of its parent container in App.js
  };

  // Style when a file is being dragged over the drop zone
  const dropZoneDraggingStyle = {
    borderColor: '#007bff',
    backgroundColor: '#e9f5ff'
  };

  // Dynamically applies dragging style
  const dropZoneActiveStyle = isDragging ? { ...dropZoneBaseStyle, ...dropZoneDraggingStyle } : dropZoneBaseStyle;

  // Style for the confirmation box shown after a logo is successfully uploaded
  const confirmationBoxStyle = {
    textAlign: 'center', 
    padding: '20px', 
    border: '1px solid #ddd', 
    borderRadius: '8px', 
    backgroundColor: '#f0fff0', 
    height: '250px', // Consistent height with the dropzone
    display: 'flex', 
    flexDirection: 'column', 
    justifyContent: 'center', 
    alignItems: 'center',
    width: '100%'
  };

  // Add blue button style for 'Upload a different logo'
  const blueButtonStyle = {
    padding: '10px 18px',
    cursor: 'pointer',
    background: 'linear-gradient(90deg, #003366 0%, #007bff 100%)',
    color: '#fff',
    border: '1.5px solid #003366',
    borderRadius: '6px',
    fontWeight: 'bold',
    fontSize: '1.1em',
    transition: 'background 0.2s, color 0.2s',
    marginTop: '20px',
    minWidth: '200px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
  };
  const blueButtonHoverStyle = {
    background: 'linear-gradient(90deg, #002244 0%, #0056b3 100%)',
  };

  return (
    // The main div of this component. Its padding is 0 because App.js handles box padding.
    <div style={{ fontFamily: 'Arial, sans-serif', width: '100%', padding: '0px' }}> 
      {!logoFile ? (
        // Display the drop zone if no logo is currently uploaded (logoFile from props is null)
        <div 
          className="logo-upload-box"
          style={{
            ...dropZoneActiveStyle,
            width: undefined // Remove fixed width so CSS can control it
          }}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => document.getElementById('logoFileInputComponent').click()} // Use a clear ID for the input
        >
          <input 
            type="file" 
            id="logoFileInputComponent" // Clear, unique ID for this component's input
            accept="image/*" 
            onChange={handleFileInputChange} 
            style={{ display: 'none' }} // Hidden, triggered by clicking the div
          />
          {/* Title and instructions are INSIDE the dropzone */}
          <h2 style={{ marginTop: 0, marginBottom: '15px'}}>
            Drag & Drop Your Logo or Click to Upload
          </h2>
          <p style={{fontSize: '1.1em', color: '#555', margin: '5px 0'}}>
            Drop your logo here, or click to browse.
          </p>
          <p style={{fontSize: '0.9em', color: '#777', margin: '5px 0'}}>
            (PNG, JPG, GIF recommended)
          </p>
        </div>
      ) : (
        // Display this confirmation if a logo IS uploaded (logoFile from props exists)
        <div style={confirmationBoxStyle}>
          {/* Move Logo Uploaded! up and center it with the button row */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', marginTop: '-64px', width: '100%' }}>
            <h2 style={{color: '#28a745', marginBottom: showInlineBlueButton ? '0' : '20px', display: 'inline-block', textAlign: 'center', minWidth: '220px'}}>
              Logo Uploaded!&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </h2>
          </div>
          {/* Only render the blue button here if not showing inline */}
          {!showInlineBlueButton && (
            <button 
              onClick={() => { onLogoSelect(null); setError(null); }} 
              style={blueButtonStyle}
              onMouseOver={e => Object.assign(e.target.style, blueButtonHoverStyle)}
              onMouseOut={e => Object.assign(e.target.style, blueButtonStyle)}
            >
              Upload a different logo
            </button>
          )}
        </div>
      )}
      {/* Render the blue button inline if requested (for alignment with Generate Mockups) */}
      {showInlineBlueButton && logoFile && (
        <button 
          onClick={() => { onLogoSelect(null); setError(null); }} 
          style={blueButtonStyle}
          onMouseOver={e => Object.assign(e.target.style, blueButtonHoverStyle)}
          onMouseOut={e => Object.assign(e.target.style, blueButtonStyle)}
        >
          Upload a different logo
        </button>
      )}

      {/* Display local validation errors if any */}
      {error && <p style={{ color: 'red', marginTop: '15px', textAlign: 'center' }}>{error}</p>}
    </div>
  );
};

export default LogoUploadPage; 